/**
 * Ships completed attention episodes to admin-api.
 *
 * Batched rather than one request per episode: episodes arrive in bursts (a run of short glances
 * away produces several within a few seconds), and a request each would be noisy for no benefit.
 *
 * **Only `attention_away` and `face_lost` are sent.** The pipeline also emits `state_change` on
 * every transition, plus session and quality events. Forwarding those would turn a log of notable
 * episodes into a continuous record of somebody's posture, which is the thing this feature is
 * explicitly not supposed to build. The server rejects them too, so the filter is defence in
 * depth rather than the only guard.
 *
 * Failures are dropped, deliberately. This is a log, not a ledger: if the network is down, the
 * right outcome is a gap in the record, not a growing queue that eventually replays a day-old
 * batch, and certainly not a retry loop that interferes with the monitoring it is reporting on.
 */

import { adminConsoleClient } from '../../../api/adminConsoleClient';
import type { AttentionEvent } from '../core/index.js';

/** Episode kinds worth persisting. Everything else stays on the device. */
const LOGGED_EVENT_TYPES: ReadonlySet<string> = new Set(['attention_away', 'face_lost']);

/** Matches the server's batch cap. */
const MAX_BATCH = 50;

/** How long to accumulate before sending, so a burst becomes one request. */
const FLUSH_INTERVAL_MS = 15_000;

/**
 * What the guard believed while the episode was open, carried for the log only.
 *
 * Episodes flush when the employee returns, so this is the only channel that reports on a period
 * during which the tab was hidden and nothing else could be sent. Without it, "absence did not
 * lock" cannot be told apart from "the guard was suspended because no frames were arriving" —
 * and those need opposite fixes.
 */
export interface GuardSnapshot {
  faceAbsentSeconds?: number;
  suspended?: boolean;
  sensorFault?: string | null;
  hidden?: boolean;
}

interface Wire {
  session_id: string;
  event_type: string;
  severity: string;
  direction: string;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  confidence: number;
  evidence: string[];
  agent_version: string;
  model_version: string;
  config_version: string;
  guard_face_absent_seconds?: number;
  guard_suspended?: boolean;
  guard_sensor_fault?: string;
  guard_surface_hidden?: boolean;
}

function toWire(event: AttentionEvent): Wire {
  return {
    session_id: event.sessionId,
    event_type: event.eventType,
    severity: event.severity,
    direction: event.direction,
    start_time: event.startTimeUtc.toISOString(),
    end_time: event.endTimeUtc.toISOString(),
    duration_seconds: event.durationSeconds,
    confidence: event.confidence,
    evidence: [...event.evidence],
    agent_version: event.agentVersion,
    model_version: event.modelVersion,
    config_version: event.configVersion,
    // `metadata` is deliberately not sent — it is an open record whose contents nobody has
    // reviewed, and the server would reject it anyway.
  };
}

export class AttentionEventReporter {
  private queue: Wire[] = [];
  private timer: ReturnType<typeof setTimeout> | undefined;
  private sending = false;

  /** Queue an episode. Ignores kinds that are not persisted. */
  record(events: readonly AttentionEvent[], guard: GuardSnapshot = {}): void {
    for (const event of events) {
      if (!LOGGED_EVENT_TYPES.has(event.eventType)) continue;
      this.queue.push({
        ...toWire(event),
        ...(guard.faceAbsentSeconds === undefined
          ? {}
          : { guard_face_absent_seconds: Math.round(guard.faceAbsentSeconds) }),
        ...(guard.suspended === undefined ? {} : { guard_suspended: guard.suspended }),
        ...(guard.sensorFault ? { guard_sensor_fault: guard.sensorFault.slice(0, 120) } : {}),
        ...(guard.hidden === undefined ? {} : { guard_surface_hidden: guard.hidden }),
      });
    }
    if (this.queue.length === 0) return;

    // Send immediately once a batch is full, otherwise wait for the window to close.
    if (this.queue.length >= MAX_BATCH) {
      void this.flush();
      return;
    }
    if (this.timer === undefined) {
      this.timer = setTimeout(() => {
        this.timer = undefined;
        void this.flush();
      }, FLUSH_INTERVAL_MS);
    }
  }

  /** Send whatever is queued. Safe to call at any time, including on teardown. */
  async flush(): Promise<void> {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    if (this.sending || this.queue.length === 0) return;

    // Taken before the await so episodes recorded during the request are not lost or duplicated.
    const batch = this.queue.splice(0, MAX_BATCH);
    this.sending = true;
    try {
      await adminConsoleClient.post('/work-focus/events', { events: batch });
    } catch {
      // Dropped on purpose — see the file header.
    } finally {
      this.sending = false;
    }
  }

  dispose(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    this.queue = [];
  }
}
